import { Fragment, memo, useMemo } from "react";
import { Link, useLocation, useSidebarStore, useUserPermissions } from "@";

const SideBarDropdown = ({
  name,
  children,
  urls,
  id,
  canShow,
  defaultOpen = false,
}) => {
  const { permissions } = useUserPermissions();
  const location = useLocation();
  const { closeSidebar } = useSidebarStore();

  const activeByLinks = useMemo(
    () =>
      urls.some(
        (link) =>
          !link.header &&
          (link.url.includes(location.pathname) ||
            location.pathname.includes(link.url)),
      ),
    [location.pathname, urls],
  );

  // Show the sub-list expanded when the current route is inside this group,
  // or when the caller asks for it to be open by default (few visible groups).
  const isOpen = activeByLinks || defaultOpen;

  const isHasPermission = useMemo(() => {
    if (canShow) {
      return canShow
        ?.split(",")
        ?.some((permission) => permissions?.[permission]);
    } else {
      return true;
    }
  }, [canShow, permissions]);

  const isSubItemActive = (url) => location.pathname.includes(url);

  return (
    <>
      {isHasPermission && (
        <li className="dropdown-sidebar relative mb-0.5">
          {/* Group trigger */}
          <button
            type="button"
            className={`${
              activeByLinks
                ? "bg-gradient-to-l from-purple-50 via-purple-50/70 to-transparent text-purple-900 dark:from-purple-950/60 dark:via-purple-900/30 dark:to-transparent dark:text-purple-200"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/60 dark:hover:text-white"
            } sidebar-li group relative flex w-full items-center rounded-2xl px-3 py-2.5 text-[15px] font-medium transition-all duration-200 ease-out`}
            aria-controls={`dropdown-sidebar-${id}`}
            data-collapse-toggle={`dropdown-sidebar-${id}`}
            data-popover-target={`popover-sidebar-${id}`}
            data-popover-placement="left"
          >
            {/* Start-side accent stripe — appears only when active */}
            {activeByLinks && (
              <span className="pointer-events-none absolute start-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-e-2xl bg-gradient-to-b from-purple-700 to-purple-950 shadow-[0_0_12px_theme(colors.purple.500/40)] dark:from-purple-400 dark:to-purple-600 dark:shadow-[0_0_14px_theme(colors.purple.500/50)]" />
            )}

            {/* Icon chip */}
            <span
              className={`sidebar-icon relative flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl transition-all duration-200 ${
                activeByLinks
                  ? "bg-gradient-to-br from-purple-800 to-purple-950 text-white dark:text-gray-300 shadow-md shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-900 dark:to-purple-950 dark:shadow-purple-900/40"
                  : "bg-gray-50 text-gray-500 ring-1 ring-gray-100 group-hover:bg-purple-50 group-hover:text-purple-800 group-hover:ring-purple-100 dark:bg-gray-800/70 dark:text-gray-400 dark:ring-gray-700/60 dark:group-hover:bg-purple-900/30 dark:group-hover:text-purple-300 dark:group-hover:ring-purple-800/40"
              }`}
            >
              {children}
            </span>

            <span
              className={`title ms-3 flex-1 text-start tracking-tight ${
                activeByLinks ? "font-semibold" : ""
              }`}
            >
              {name}
            </span>

            <svg
              className={`arrow-icon h-2.5 w-2.5 transition-transform duration-300 ${
                isOpen ? "rotate-180" : "opacity-60 group-hover:opacity-100"
              }`}
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 4 4 4-4"
              />
            </svg>
          </button>

          {/* Popover (collapsed-mode flyout) */}
          <div
            data-popover
            id={`popover-sidebar-${id}`}
            role="tooltip"
            className="popover-sidebar invisible absolute z-[55] hidden w-64 overflow-hidden rounded-2xl border border-gray-100 bg-white/95 text-sm text-gray-500 opacity-0 shadow-xl shadow-gray-900/5 backdrop-blur-xl transition-[opacity,transform] duration-200 dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-400 dark:shadow-black/40 [.small_&]:start-[calc(100%+10px)] [.small_&]:top-0 [.small_&]:-translate-x-1 rtl:[.small_&]:translate-x-1 [.small_.dropdown-sidebar:hover_&]:!visible [.small_.dropdown-sidebar:hover_&]:!block [.small_.dropdown-sidebar:hover_&]:translate-x-0 [.small_.dropdown-sidebar:hover_&]:!opacity-100 rtl:[.small_.dropdown-sidebar:hover_&]:translate-x-0"
          >
            <div className="border-b border-gray-100 bg-gradient-to-l from-purple-50/60 to-transparent px-4 py-3 dark:border-gray-700/60 dark:from-purple-950/40">
              <h3 className="text-[15px] font-semibold tracking-tight text-gray-900 dark:text-gray-200">
                {name}
              </h3>
            </div>

            <div className="space-y-0.5 p-2">
              {urls.map((link, index) => {
                const allowed = link?.canShow
                  ? link.canShow
                      .split(",")
                      .some((permission) => permissions?.[permission])
                  : true;
                if (!allowed) return null;

                if (link.header) {
                  return (
                    <div
                      key={`header-${index}`}
                      className="px-2.5 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 first:pt-1 dark:text-gray-500"
                    >
                      {link.name}
                    </div>
                  );
                }

                const active = isSubItemActive(link.url);
                return (
                  <Link
                    key={`${link.url}-${index}`}
                    to={link.url}
                    onClick={closeSidebar}
                    className={`group/item flex items-center gap-2.5 rounded-2xl px-2.5 py-2 text-[14px] transition-colors ${
                      active
                        ? "bg-purple-50 font-semibold text-purple-900 dark:bg-purple-950/60 dark:text-purple-300"
                        : "text-gray-600 hover:bg-gray-50 hover:text-purple-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-purple-300"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-2xl transition-colors ${
                        active
                          ? "bg-purple-700 dark:bg-purple-400"
                          : "bg-gray-300 group-hover/item:bg-purple-500 dark:bg-gray-600"
                      }`}
                    />
                    {link.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Expanded sub-list */}
          <ul
            id={`dropdown-sidebar-${id}`}
            className={`${
              isOpen ? "" : "hidden"
            } dropdown-sidebar-ul relative ms-[26px] mt-1 space-y-0.5 border-s border-gray-200/80 ps-3 dark:border-gray-700/60`}
          >
            {urls.map((link, index) => {
              const allowed = link?.canShow
                ? link.canShow
                    .split(",")
                    .some((permission) => permissions?.[permission])
                : true;
              if (!allowed) return null;

              if (link.header) {
                return (
                  <li
                    key={`header-${index}`}
                    className="relative px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 first:pt-0 dark:text-gray-500"
                  >
                    {/* Cap the connector line at the header so it doesn't pass through it */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -start-[1px] top-0 h-full w-px bg-white dark:bg-gray-900"
                    />
                    <span className="relative">{link.name}</span>
                  </li>
                );
              }

              const active = isSubItemActive(link.url);

              return (
                <Fragment key={`${link.url}-${index}`}>
                  <li className="relative">
                    {/* Connector dot on the timeline */}
                    <span
                      className={`pointer-events-none absolute -start-[15px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-2xl ring-4 transition-all duration-200 ${
                        active
                          ? "bg-purple-700 ring-purple-100 dark:bg-purple-400 dark:ring-purple-950/60"
                          : "bg-gray-300 ring-white dark:bg-gray-600 dark:ring-gray-900"
                      }`}
                    />
                    <Link
                      to={link.url}
                      onClick={closeSidebar}
                      className={`sidebar-li group flex w-full items-center rounded-2xl px-3 py-2 text-[14px] transition-colors ${
                        active
                          ? "bg-purple-50/70 font-semibold text-purple-900 dark:bg-purple-950/20 dark:text-purple-200"
                          : "text-gray-500 hover:bg-gray-50 hover:text-purple-900 dark:text-gray-400 dark:hover:bg-gray-800/50 dark:hover:text-purple-300"
                      }`}
                    >
                      {link.name}
                    </Link>
                  </li>
                </Fragment>
              );
            })}
          </ul>
        </li>
      )}
    </>
  );
};

export default memo(SideBarDropdown);
